import React, { useState, useEffect, useRef } from 'react';
import api from '../utils/api';
import './AnalyticsTab.css';

/**
 * Analytics Tab Component for Parent Dashboard
 * Adapted from AnalyticsDashboard.jsx reference
 * Integrated with existing MSKICA styling
 */

const AnalyticsTab = ({ kids = [], user }) => {
  const [activeSubTab, setActiveSubTab] = useState('reports');
  const [showNewAnalysisModal, setShowNewAnalysisModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedReport, setSelectedReport] = useState(null);
  const [newAnalysisStep, setNewAnalysisStep] = useState(1);
  const [newAnalysisSource, setNewAnalysisSource] = useState(null);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [selectedSkillFilter, setSelectedSkillFilter] = useState('all');
  const [selectedStatusFilter, setSelectedStatusFilter] = useState('all');
  const filterDropdownRef = useRef(null);
  const [newAnalysisData, setNewAnalysisData] = useState({
    player: '',
    skill: '',
    url: '',
    file: null,
    notes: ''
  });

  // Data from API
  const [reports, setReports] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [stats, setStats] = useState({
    publishedReports: 0,
    inProgress: 0,
    thisMonth: 0
  });
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [dataError, setDataError] = useState(null);

  // Fetch analysis requests from API
  const fetchAnalysisRequests = async () => {
    try {
      setIsLoadingData(true);
      setDataError(null);
      const response = await api.get('/api/analysis-requests');
      
      if (response.data.success) {
        setReports(response.data.reports || []);
        setSubmissions(response.data.submissions || []);
        setStats(response.data.stats || {
          publishedReports: 0,
          inProgress: 0,
          thisMonth: 0
        });
      }
    } catch (error) {
      console.error('Failed to fetch analysis requests:', error);
      setDataError('Failed to load analysis data. Please try again.');
    } finally {
      setIsLoadingData(false);
    }
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchAnalysisRequests();
  }, []);

  // Workflow stages configuration
  const workflowStages = [
    { id: 'uploaded', label: 'Video Uploaded', icon: '📤', color: '#6B7280' },
    { id: 'submitted', label: 'Submitted for Review', icon: '🕐', color: '#3B82F6' },
    { id: 'pending_coach', label: 'Pending Coach Review', icon: '👁️', color: '#F59E0B' },
    { id: 'review_complete', label: 'Review Completed', icon: '✅', color: '#8B5CF6' },
    { id: 'published', label: 'Report Published', icon: '📄', color: '#10B981' }
  ];

  const skillOptions = ['Batting', 'LegSpin', 'FastBowling', 'OffSpin', 'WicketKeeping', 'Generic (Fielding)'];
  
  const sourceOptions = [
    { id: 'youtube', label: 'YouTube Video', icon: '🎥', color: '#EF4444', desc: 'Paste a YouTube URL', disabled: true },
    { id: 'gdrive', label: 'Google Drive', icon: '💾', color: '#3B82F6', desc: 'Select from your Drive', disabled: true },
    { id: 'local', label: 'Upload File', icon: '📤', color: '#8B5CF6', desc: 'Upload from your device', disabled: false }
  ];

  // File upload state
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Filter data based on search, skill, and status
  const filteredReports = reports.filter(report => {
    const matchesSearch = report.playerName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          report.skill.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesSkill = selectedSkillFilter === 'all' || 
                         report.skill.toLowerCase() === selectedSkillFilter.toLowerCase();
    const matchesStatus = selectedStatusFilter === 'all' || 
                          report.status === selectedStatusFilter;
    return matchesSearch && matchesSkill && matchesStatus;
  });

  const filteredSubmissions = submissions.filter(submission => {
    const matchesSearch = submission.playerName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          submission.skill.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesSkill = selectedSkillFilter === 'all' || 
                         submission.skill.toLowerCase() === selectedSkillFilter.toLowerCase();
    const matchesStatus = selectedStatusFilter === 'all' || 
                          submission.status === selectedStatusFilter;
    return matchesSearch && matchesSkill && matchesStatus;
  });

  // Calculate stats
  const thisMonthReports = reports.filter(r => {
    const reportDate = new Date(r.datePublished);
    const now = new Date();
    return reportDate.getMonth() === now.getMonth() && reportDate.getFullYear() === now.getFullYear();
  }).length;

  // Close filter dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(event.target)) {
        setShowFilterDropdown(false);
      }
    };

    if (showFilterDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showFilterDropdown]);

  // Handle modal close
  const handleCloseModal = () => {
    setShowNewAnalysisModal(false);
    setNewAnalysisStep(1);
    setNewAnalysisSource(null);
    setNewAnalysisData({ player: '', skill: '', url: '', file: null, notes: '' });
    setUploadProgress(0);
    setIsUploading(false);
  };

  // Handle file selection
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ['video/mp4', 'video/mov', 'video/avi', 'video/quicktime', 'video/x-msvideo'];
      if (!validTypes.includes(file.type)) {
        alert('Please select a valid video file (MP4, MOV, or AVI)');
        return;
      }
      // Validate file size (500MB max)
      if (file.size > 500 * 1024 * 1024) {
        alert('File size must be less than 500MB');
        return;
      }
      setNewAnalysisData({ ...newAnalysisData, file });
    }
  };

  // Handle file drop
  const handleFileDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      const validTypes = ['video/mp4', 'video/mov', 'video/avi', 'video/quicktime', 'video/x-msvideo'];
      if (!validTypes.includes(file.type)) {
        alert('Please select a valid video file (MP4, MOV, or AVI)');
        return;
      }
      if (file.size > 500 * 1024 * 1024) {
        alert('File size must be less than 500MB');
        return;
      }
      setNewAnalysisData({ ...newAnalysisData, file });
    }
  };

  // Format file name for upload: skill_yyyy_mm_dd
  const formatFileName = (skill, originalFileName) => {
    const now = new Date();
    const dateStr = `${now.getFullYear()}_${String(now.getMonth() + 1).padStart(2, '0')}_${String(now.getDate()).padStart(2, '0')}`;
    const extension = originalFileName.split('.').pop();
    const skillFormatted = skill.replace(/\s+/g, '_');
    return `${skillFormatted}_${dateStr}.${extension}`;
  };

  // Handle analysis submission
  const handleSubmitAnalysis = async () => {
    try {
      setIsUploading(true);
      setUploadProgress(10);

      // Get selected player info for folder creation
      const selectedPlayer = kids.find(k => 
        `${k.first_name} ${k.last_name}` === newAnalysisData.player
      );

      if (!selectedPlayer) {
        alert('Please select a valid player');
        setIsUploading(false);
        return;
      }

      if (newAnalysisSource === 'local' && newAnalysisData.file) {
        // Format the filename: skill_yyyy_mm_dd
        const formattedFileName = formatFileName(newAnalysisData.skill, newAnalysisData.file.name);
        
        // Create folder name: player_id or player_name
        const folderName = `${selectedPlayer.id}_${selectedPlayer.first_name}_${selectedPlayer.last_name}`;

        // Create FormData for file upload
        const formData = new FormData();
        formData.append('video', newAnalysisData.file, formattedFileName);
        formData.append('folderName', folderName);
        formData.append('playerId', selectedPlayer.id);
        formData.append('playerName', `${selectedPlayer.first_name} ${selectedPlayer.last_name}`);
        formData.append('skill', newAnalysisData.skill);
        formData.append('notes', newAnalysisData.notes || '');

        setUploadProgress(30);

        // Upload to Google Drive via backend
        // Note: Do NOT manually set Content-Type for multipart/form-data.
        // Axios auto-sets it with the correct boundary when data is FormData.
        const response = await api.post('/api/upload-video-to-drive', formData, {
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 70) / progressEvent.total) + 30;
            setUploadProgress(Math.min(percentCompleted, 95));
          },
        });

        setUploadProgress(100);

        if (response.data.success) {
          // Log Airtable result for debugging
          if (response.data.airtableRecordId) {
            console.log('Airtable record created:', response.data.airtableRecordId);
          } else if (response.data.airtableError) {
            console.warn('Airtable record failed:', response.data.airtableError);
          }
          
          // Refresh data from API to get the new submission
          await fetchAnalysisRequests();

          alert('Video uploaded successfully! You\'ll be notified when the analysis is ready.');
          handleCloseModal();
        } else {
          throw new Error(response.data.error || 'Upload failed');
        }
      } else {
        // Handle non-file submissions (should not happen with current UI)
        alert('Please upload a video file');
      }
    } catch (error) {
      console.error('Error submitting analysis:', error);
      alert(`Failed to upload video: ${error.message || 'Please try again'}`);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  // Handle download report
  const handleDownloadReport = (report) => {
    // Create a text summary of the report
    const reportContent = `
MSKICA - Analysis Report
========================

Player: ${report.playerName}
Skill: ${report.skill}
Date Published: ${new Date(report.datePublished).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
Analyzed By: ${report.analyzedBy}

SUMMARY:
${report.summary}

STRENGTHS:
${report.strengths.map((s, i) => `${i + 1}. ${s}`).join('\n')}

PRIORITY AREAS FOR IMPROVEMENT:
${report.priorities.map((p, i) => `${i + 1}. ${p}`).join('\n')}

Video URL: ${report.videoUrl}
    `;

    // Create a blob and download
    const blob = new Blob([reportContent], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${report.playerName}_${report.skill}_Analysis_Report.txt`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const stage = workflowStages.find(s => s.id === status);
    if (!stage) return null;
    
    return (
      <span 
        className="analytics-status-badge"
        style={{ 
          backgroundColor: `${stage.color}15`,
          color: stage.color,
          border: `1px solid ${stage.color}30`
        }}
      >
        <span className="status-icon">{stage.icon}</span>
        {stage.label}
      </span>
    );
  };

  // Render workflow progress
  const renderWorkflowProgress = (currentStatus) => {
    const currentIndex = workflowStages.findIndex(s => s.id === currentStatus);
    
    return (
      <div className="workflow-progress">
        {workflowStages.map((stage, index) => {
          const isComplete = index <= currentIndex;
          const isCurrent = index === currentIndex;
          
          return (
            <React.Fragment key={stage.id}>
              <div 
                className={`workflow-step ${isCurrent ? 'current' : ''}`}
                style={{ 
                  backgroundColor: isComplete ? stage.color : '#E5E7EB',
                  boxShadow: isCurrent ? `0 0 0 3px ${stage.color}30` : 'none'
                }}
                title={stage.label}
              >
                <span style={{ filter: isComplete ? 'brightness(2)' : 'none' }}>
                  {stage.icon}
                </span>
              </div>
              {index < workflowStages.length - 1 && (
                <div 
                  className="workflow-connector"
                  style={{ 
                    backgroundColor: index < currentIndex ? workflowStages[index + 1].color : '#E5E7EB'
                  }}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  // Loading state
  if (isLoadingData) {
    return (
      <div className="analytics-content">
        <div className="analytics-header">
          <div className="analytics-title-section">
            <h2 className="analytics-main-title">Analytics & Skill Development</h2>
            <p className="analytics-subtitle">AI-powered video analysis and progress tracking</p>
          </div>
        </div>
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
          <p style={{ color: '#6B7280', fontSize: '16px' }}>Loading analysis data...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (dataError) {
    return (
      <div className="analytics-content">
        <div className="analytics-header">
          <div className="analytics-title-section">
            <h2 className="analytics-main-title">Analytics & Skill Development</h2>
            <p className="analytics-subtitle">AI-powered video analysis and progress tracking</p>
          </div>
        </div>
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
          <p style={{ color: '#EF4444', fontSize: '16px', marginBottom: '16px' }}>{dataError}</p>
          <button 
            onClick={fetchAnalysisRequests}
            style={{
              backgroundColor: '#10B981',
              color: 'white',
              border: 'none',
              padding: '10px 24px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="analytics-content">
      {/* Header Section */}
      <div className="analytics-header">
        <div className="analytics-title-section">
          <h2 className="analytics-main-title">Analytics & Skill Development</h2>
          <p className="analytics-subtitle">AI-powered video analysis and progress tracking</p>
        </div>
        <button 
          className="new-analysis-btn"
          onClick={() => setShowNewAnalysisModal(true)}
        >
          <span className="btn-icon">➕</span>
          New Analysis
        </button>
      </div>

      {/* Stats Row */}
      <div className="analytics-stats-card">
        <div className="stat-item">
          <div className="stat-icon" style={{ backgroundColor: '#10B98115' }}>
            <span style={{ color: '#10B981' }}>📄</span>
          </div>
          <div className="stat-content">
            <div className="stat-value">{reports.length}</div>
            <div className="stat-label">Published Reports</div>
          </div>
        </div>
        <div className="stat-divider"></div>
        <div className="stat-item">
          <div className="stat-icon" style={{ backgroundColor: '#F59E0B15' }}>
            <span style={{ color: '#F59E0B' }}>⏳</span>
          </div>
          <div className="stat-content">
            <div className="stat-value">{submissions.length}</div>
            <div className="stat-label">In Progress</div>
          </div>
        </div>
        <div className="stat-divider"></div>
        <div className="stat-item">
          <div className="stat-icon" style={{ backgroundColor: '#3B82F615' }}>
            <span style={{ color: '#3B82F6' }}>📅</span>
          </div>
          <div className="stat-content">
            <div className="stat-value">{thisMonthReports}</div>
            <div className="stat-label">This Month</div>
          </div>
        </div>
      </div>

      {/* Sub Navigation and Search */}
      <div className="analytics-controls">
        <div className="analytics-tabs">
          <button
            className={`analytics-tab ${activeSubTab === 'reports' ? 'active' : ''}`}
            onClick={() => setActiveSubTab('reports')}
          >
            Published Reports
            <span className="tab-count">{filteredReports.length}</span>
          </button>
          <button
            className={`analytics-tab ${activeSubTab === 'pending' ? 'active' : ''}`}
            onClick={() => setActiveSubTab('pending')}
          >
            In Progress
            <span className="tab-count">{filteredSubmissions.length}</span>
          </button>
        </div>

        <div className="analytics-search-section">
          <div className="search-input-wrapper">
            <span className="search-icon">🔍</span>
            <input
              type="text"
              placeholder="Search reports..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
          </div>
          <div className="filter-dropdown-wrapper" ref={filterDropdownRef}>
            <button 
              className={`filter-btn ${(selectedSkillFilter !== 'all' || selectedStatusFilter !== 'all') ? 'active' : ''}`}
              onClick={() => setShowFilterDropdown(!showFilterDropdown)}
              title="Filter"
            >
              <span>⚙️</span>
              {(selectedSkillFilter !== 'all' || selectedStatusFilter !== 'all') && (
                <span className="filter-badge"></span>
              )}
            </button>
            {showFilterDropdown && (
              <div className="filter-dropdown">
                <div className="filter-section">
                  <h4 className="filter-section-title">Filter by Skill</h4>
                  <div className="filter-options">
                    <button
                      className={`filter-option ${selectedSkillFilter === 'all' ? 'active' : ''}`}
                      onClick={() => setSelectedSkillFilter('all')}
                    >
                      All Skills
                    </button>
                    {skillOptions.map(skill => (
                      <button
                        key={skill}
                        className={`filter-option ${selectedSkillFilter.toLowerCase() === skill.toLowerCase() ? 'active' : ''}`}
                        onClick={() => setSelectedSkillFilter(skill)}
                      >
                        {skill}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="filter-section">
                  <h4 className="filter-section-title">Filter by Status</h4>
                  <div className="filter-options">
                    <button
                      className={`filter-option ${selectedStatusFilter === 'all' ? 'active' : ''}`}
                      onClick={() => setSelectedStatusFilter('all')}
                    >
                      All Statuses
                    </button>
                    {workflowStages.map(stage => (
                      <button
                        key={stage.id}
                        className={`filter-option ${selectedStatusFilter === stage.id ? 'active' : ''}`}
                        onClick={() => setSelectedStatusFilter(stage.id)}
                      >
                        <span style={{ marginRight: '0.375rem' }}>{stage.icon}</span>
                        {stage.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="filter-actions">
                  <button
                    className="filter-clear-btn"
                    onClick={() => {
                      setSelectedSkillFilter('all');
                      setSelectedStatusFilter('all');
                    }}
                  >
                    Clear Filters
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Content Grid */}
      <div className="analytics-grid">
        {activeSubTab === 'reports' && filteredReports.map(report => (
          <div key={report.id} className="analytics-card report-card">
            <div className="card-header">
              <div className="player-info">
                <h3 className="player-name">{report.playerName}</h3>
                <div className="card-meta">
                  <span className="meta-item">
                    <span className="meta-icon">🏏</span>
                    {report.skill} Analysis
                  </span>
                  <span className="meta-item">
                    <span className="meta-icon">📅</span>
                    {new Date(report.datePublished).toLocaleDateString('en-US', { 
                      month: 'short', day: 'numeric', year: 'numeric' 
                    })}
                  </span>
                </div>
              </div>
              {getStatusBadge(report.status)}
            </div>

            <p className="report-summary">{report.summary}</p>

            <div className="strengths-tags">
              {report.strengths.slice(0, 3).map((strength, i) => (
                <span key={i} className="strength-tag">{strength}</span>
              ))}
            </div>

            <div className="card-actions">
              <button 
                className="view-report-btn"
                onClick={() => setSelectedReport(report)}
              >
                <span className="btn-icon">👁️</span>
                View Report
              </button>
              <button 
                className="icon-btn" 
                title="Download Report"
                onClick={() => handleDownloadReport(report)}
              >
                <span>⬇️</span>
              </button>
              <a 
                href={report.videoUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="icon-btn"
                title="Watch Video"
              >
                <span>🎥</span>
              </a>
            </div>
          </div>
        ))}

        {activeSubTab === 'pending' && filteredSubmissions.map(submission => {
          const source = sourceOptions.find(s => s.id === submission.source);
          return (
            <div key={submission.id} className="analytics-card submission-card">
              <div className="card-header">
                <div className="player-info-with-icon">
                  <div 
                    className="source-icon"
                    style={{ backgroundColor: `${source.color}15` }}
                  >
                    <span style={{ fontSize: '20px' }}>{source.icon}</span>
                  </div>
                  <div>
                    <h3 className="player-name">{submission.playerName}</h3>
                    <p className="skill-label">{submission.skill} Analysis</p>
                  </div>
                </div>
                {getStatusBadge(submission.status)}
              </div>

              <div className="progress-section">
                <div className="progress-header">
                  <span className="progress-label">Processing Progress</span>
                  <span className="progress-value">{submission.progress}%</span>
                </div>
                <div className="progress-bar">
                  <div 
                    className="progress-fill"
                    style={{ width: `${submission.progress}%` }}
                  />
                </div>
              </div>

              {renderWorkflowProgress(submission.status)}

              <div className="submission-footer">
                Submitted on {new Date(submission.dateSubmitted).toLocaleDateString('en-US', { 
                  month: 'short', day: 'numeric', year: 'numeric' 
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Empty States */}
      {activeSubTab === 'reports' && filteredReports.length === 0 && (
        <div className="empty-state">
          <div className="empty-icon">📄</div>
          <h3 className="empty-title">No Reports Yet</h3>
          <p className="empty-text">Submit your first video for analysis to get started.</p>
          <button 
            className="new-analysis-btn"
            onClick={() => setShowNewAnalysisModal(true)}
          >
            Submit First Video
          </button>
        </div>
      )}

      {activeSubTab === 'pending' && filteredSubmissions.length === 0 && (
        <div className="empty-state">
          <div className="empty-icon">⏳</div>
          <h3 className="empty-title">No Pending Submissions</h3>
          <p className="empty-text">All your submissions have been processed.</p>
        </div>
      )}

      {/* Workflow Legend */}
      <div className="workflow-legend-card">
        <h3 className="legend-title">Analysis Workflow</h3>
        <div className="legend-items">
          {workflowStages.map((stage, i) => (
            <div key={stage.id} className="legend-item">
              <div 
                className="legend-icon"
                style={{ backgroundColor: `${stage.color}15` }}
              >
                <span style={{ color: stage.color }}>{stage.icon}</span>
              </div>
              <span className="legend-label">{stage.label}</span>
              {i < workflowStages.length - 1 && (
                <span className="legend-arrow">→</span>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* New Analysis Modal */}
      {showNewAnalysisModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="analytics-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-header-content">
                <div>
                  <h2 className="modal-title">Submit New Analysis</h2>
                  <p className="modal-step-indicator">Step {newAnalysisStep} of 3</p>
                </div>
                <button className="modal-close-btn" onClick={handleCloseModal}>
                  ✕
                </button>
              </div>
              <div className="modal-progress-bar">
                {[1, 2, 3].map(s => (
                  <div 
                    key={s}
                    className={`progress-segment ${s <= newAnalysisStep ? 'active' : ''}`}
                  />
                ))}
              </div>
            </div>

            <div className="modal-body">
              {/* Step 1: Choose Source */}
              {newAnalysisStep === 1 && (
                <div className="modal-step">
                  <h3 className="step-title">Choose Video Source</h3>
                  <div className="source-options">
                    {sourceOptions.map(source => (
                      <button
                        key={source.id}
                        onClick={() => { 
                          if (!source.disabled) {
                            setNewAnalysisSource(source.id); 
                            setNewAnalysisStep(2); 
                          }
                        }}
                        className={`source-option ${source.disabled ? 'source-option-disabled' : ''}`}
                        disabled={source.disabled}
                        style={source.disabled ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                      >
                        <div 
                          className="source-option-icon"
                          style={{ backgroundColor: `${source.color}15` }}
                        >
                          <span style={{ fontSize: '2rem', color: source.color }}>{source.icon}</span>
                        </div>
                        <div className="source-option-content">
                          <div className="source-option-label">
                            {source.label}
                            {source.disabled && <span style={{ marginLeft: '8px', fontSize: '0.75rem', color: '#94a3b8' }}>(Coming Soon)</span>}
                          </div>
                          <div className="source-option-desc">{source.desc}</div>
                        </div>
                        <span className="source-option-arrow">{source.disabled ? '🔒' : '→'}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Step 2: Video Details */}
              {newAnalysisStep === 2 && (
                <div className="modal-step">
                  <h3 className="step-title">Video Details</h3>
                  
                  <div className="form-group-modern">
                    <label className="form-label-modern">Select Player</label>
                    <select 
                      value={newAnalysisData.player}
                      onChange={e => setNewAnalysisData({...newAnalysisData, player: e.target.value})}
                      className="form-select-modern"
                    >
                      <option value="">Choose a player...</option>
                      {kids.map(kid => (
                        <option key={kid.id} value={`${kid.first_name} ${kid.last_name}`}>
                          {kid.first_name} {kid.last_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group-modern">
                    <label className="form-label-modern">Skill to Analyze</label>
                    <div className="skill-chips">
                      {skillOptions.map(skill => (
                        <button
                          key={skill}
                          onClick={() => setNewAnalysisData({...newAnalysisData, skill})}
                          className={`skill-chip ${newAnalysisData.skill === skill ? 'active' : ''}`}
                        >
                          {skill}
                        </button>
                      ))}
                    </div>
                  </div>

                  {newAnalysisSource === 'youtube' && (
                    <div className="form-group-modern">
                      <label className="form-label-modern">YouTube URL</label>
                      <input
                        type="url"
                        value={newAnalysisData.url}
                        onChange={e => setNewAnalysisData({...newAnalysisData, url: e.target.value})}
                        placeholder="https://www.youtube.com/watch?v=..."
                        className="form-input-modern"
                      />
                    </div>
                  )}

                  {newAnalysisSource === 'gdrive' && (
                    <div className="form-group-modern">
                      <label className="form-label-modern">Google Drive Link</label>
                      <input
                        type="url"
                        value={newAnalysisData.url}
                        onChange={e => setNewAnalysisData({...newAnalysisData, url: e.target.value})}
                        placeholder="https://drive.google.com/file/d/..."
                        className="form-input-modern"
                      />
                      <p style={{ fontSize: '0.8125rem', color: '#64748b', marginTop: '0.5rem', marginBottom: 0 }}>
                        Make sure the video is shared with "Anyone with the link"
                      </p>
                    </div>
                  )}

                  {newAnalysisSource === 'local' && (
                    <div 
                      className={`upload-area ${newAnalysisData.file ? 'has-file' : ''}`}
                      onClick={() => fileInputRef.current?.click()}
                      onDrop={handleFileDrop}
                      onDragOver={(e) => e.preventDefault()}
                    >
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileSelect}
                        accept="video/mp4,video/mov,video/avi,video/quicktime,video/x-msvideo"
                        style={{ display: 'none' }}
                      />
                      {newAnalysisData.file ? (
                        <>
                          <span className="upload-icon">✅</span>
                          <p className="upload-text">{newAnalysisData.file.name}</p>
                          <p className="upload-subtext">
                            {(newAnalysisData.file.size / (1024 * 1024)).toFixed(2)} MB
                          </p>
                          <p className="upload-hint">Click to change file</p>
                        </>
                      ) : (
                        <>
                          <span className="upload-icon">📤</span>
                          <p className="upload-text">Drop your video here</p>
                          <p className="upload-subtext">or click to browse</p>
                          <p className="upload-hint">MP4, MOV, AVI up to 500MB</p>
                        </>
                      )}
                    </div>
                  )}

                  <div className="modal-actions">
                    <button
                      onClick={() => setNewAnalysisStep(1)}
                      className="modal-btn-secondary"
                    >
                      Back
                    </button>
                    <button
                      onClick={() => setNewAnalysisStep(3)}
                      disabled={
                        !newAnalysisData.player || 
                        !newAnalysisData.skill ||
                        ((newAnalysisSource === 'youtube' || newAnalysisSource === 'gdrive') && !newAnalysisData.url) ||
                        (newAnalysisSource === 'local' && !newAnalysisData.file)
                      }
                      className="modal-btn-primary"
                    >
                      Continue
                    </button>
                  </div>
                </div>
              )}

              {/* Step 3: Review & Submit */}
              {newAnalysisStep === 3 && (
                <div className="modal-step">
                  <h3 className="step-title">Review & Submit</h3>
                  
                  <div className="review-section">
                    <div className="review-item">
                      <span className="review-label">Player</span>
                      <span className="review-value">{newAnalysisData.player}</span>
                    </div>
                    <div className="review-item">
                      <span className="review-label">Skill</span>
                      <span className="review-value">{newAnalysisData.skill}</span>
                    </div>
                    <div className="review-item">
                      <span className="review-label">Source</span>
                      <span className="review-value">
                        {sourceOptions.find(s => s.id === newAnalysisSource)?.label || newAnalysisSource}
                      </span>
                    </div>
                    {newAnalysisSource === 'local' && newAnalysisData.file && (
                      <div className="review-item">
                        <span className="review-label">Video File</span>
                        <span className="review-value" style={{ 
                          fontSize: '0.8125rem', 
                          wordBreak: 'break-all',
                          color: '#10B981'
                        }}>
                          📹 {formatFileName(newAnalysisData.skill, newAnalysisData.file.name)} ({(newAnalysisData.file.size / (1024 * 1024)).toFixed(2)} MB)
                        </span>
                      </div>
                    )}
                    {(newAnalysisSource === 'youtube' || newAnalysisSource === 'gdrive') && newAnalysisData.url && (
                      <div className="review-item">
                        <span className="review-label">Video URL</span>
                        <span className="review-value" style={{ 
                          fontSize: '0.8125rem', 
                          wordBreak: 'break-all',
                          color: '#3B82F6'
                        }}>
                          {newAnalysisData.url}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="form-group-modern">
                    <label className="form-label-modern">Additional Notes (Optional)</label>
                    <textarea
                      value={newAnalysisData.notes}
                      onChange={e => setNewAnalysisData({...newAnalysisData, notes: e.target.value})}
                      placeholder="Any specific areas you'd like analyzed..."
                      rows={3}
                      className="form-textarea-modern"
                      disabled={isUploading}
                    />
                  </div>

                  {isUploading && (
                    <div className="upload-progress-section" style={{ marginBottom: '1rem' }}>
                      <div className="upload-progress-bar" style={{
                        width: '100%',
                        height: '8px',
                        backgroundColor: '#e2e8f0',
                        borderRadius: '4px',
                        overflow: 'hidden'
                      }}>
                        <div style={{
                          width: `${uploadProgress}%`,
                          height: '100%',
                          backgroundColor: '#3B82F6',
                          borderRadius: '4px',
                          transition: 'width 0.3s ease'
                        }} />
                      </div>
                      <p style={{ fontSize: '0.875rem', color: '#64748b', marginTop: '0.5rem', textAlign: 'center' }}>
                        Uploading... {uploadProgress}%
                      </p>
                    </div>
                  )}

                  <div className="alert-info">
                    <span className="alert-icon">ℹ️</span>
                    <div>
                      <p className="alert-title">Processing Time</p>
                      <p className="alert-text">Analysis typically takes 24-48 hours. You'll receive an email when the report is ready.</p>
                    </div>
                  </div>

                  <div className="modal-actions">
                    <button
                      onClick={() => setNewAnalysisStep(2)}
                      className="modal-btn-secondary"
                      disabled={isUploading}
                    >
                      Back
                    </button>
                    <button
                      onClick={handleSubmitAnalysis}
                      className="modal-btn-primary"
                      disabled={isUploading}
                    >
                      <span className="btn-icon">{isUploading ? '⏳' : '▶️'}</span>
                      {isUploading ? 'Uploading...' : 'Submit for Analysis'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* View Report Modal */}
      {selectedReport && (
        <div className="modal-overlay" onClick={() => setSelectedReport(null)}>
          <div className="report-detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-header-content">
                <div>
                  <h2 className="modal-title">Analysis Report</h2>
                  <p className="modal-step-indicator">{selectedReport.playerName} - {selectedReport.skill}</p>
                </div>
                <button className="modal-close-btn" onClick={() => setSelectedReport(null)}>
                  ✕
                </button>
              </div>
            </div>

            <div className="modal-body">
              <div className="report-detail-section">
                <div className="report-meta-grid">
                  <div className="report-meta-item">
                    <span className="meta-label">Date Published</span>
                    <span className="meta-value">
                      {new Date(selectedReport.datePublished).toLocaleDateString('en-US', { 
                        month: 'long', day: 'numeric', year: 'numeric' 
                      })}
                    </span>
                  </div>
                  <div className="report-meta-item">
                    <span className="meta-label">Analyzed By</span>
                    <span className="meta-value">{selectedReport.analyzedBy}</span>
                  </div>
                  <div className="report-meta-item">
                    <span className="meta-label">Status</span>
                    {getStatusBadge(selectedReport.status)}
                  </div>
                </div>

                <div className="report-section">
                  <h3 className="section-title">Summary</h3>
                  <p className="section-content">{selectedReport.summary}</p>
                </div>

                <div className="report-section">
                  <h3 className="section-title">Key Strengths</h3>
                  <ul className="report-list">
                    {selectedReport.strengths.map((strength, i) => (
                      <li key={i} className="report-list-item">
                        <span className="list-icon">✓</span>
                        {strength}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="report-section">
                  <h3 className="section-title">Priority Areas for Improvement</h3>
                  <ul className="report-list priority-list">
                    {selectedReport.priorities.map((priority, i) => (
                      <li key={i} className="report-list-item">
                        <span className="list-number">{i + 1}</span>
                        {priority}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="report-actions">
                  <button 
                    className="modal-btn-primary"
                    onClick={() => handleDownloadReport(selectedReport)}
                  >
                    <span className="btn-icon">⬇️</span>
                    Download Report
                  </button>
                  <a 
                    href={selectedReport.videoUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="modal-btn-secondary"
                  >
                    <span className="btn-icon">🎥</span>
                    Watch Video
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalyticsTab;
